import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Command, Loader2, ArrowLeft } from "lucide-react";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ full_name: "", email: "", password: "", role: "candidate" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user =
        mode === "login"
          ? await login(form.email, form.password)
          : await register(form);
      navigate(user.role === "internal" ? "/app" : "/portal");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white text-[#09090B]">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-14 border-r border-black/10 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-40" />
        <Link to="/" className="relative flex items-center gap-2 text-muted-foreground hover:text-[#09090B] transition-colors text-sm">
          <ArrowLeft className="h-4 w-4" /> Back to site
        </Link>
        <div className="relative">
          <div className="h-10 w-10 bg-primary flex items-center justify-center mb-8">
            <Command className="h-5 w-5" />
          </div>
          <h1 className="font-heading font-black text-5xl tracking-tight leading-[0.95]">
            The search<br />operating system.
          </h1>
          <p className="text-muted-foreground mt-6 max-w-sm">
            One sign-in. Your role determines your workspace — recruiter, client, or candidate.
          </p>
        </div>
        <p className="relative overline">Innovation Talent Partners</p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-14">
        <div className="w-full max-w-sm">
          <div className="flex gap-6 mb-8 border-b border-black/10">
            {["login", "register"].map((m) => (
              <button
                key={m}
                data-testid={`tab-${m}`}
                onClick={() => { setMode(m); setError(""); }}
                className={`pb-3 text-sm font-medium capitalize transition-colors -mb-px border-b-2 ${
                  mode === m ? "border-primary text-[#09090B]" : "border-transparent text-muted-foreground hover:text-[#09090B]"
                }`}
              >
                {m === "login" ? "Sign In" : "Create Account"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="full_name">Full name</Label>
                <Input id="full_name" data-testid="input-fullname" value={form.full_name} onChange={set("full_name")} required className="bg-[#F4F4F5] border-black/10 rounded-none h-11" />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="input-email" value={form.email} onChange={set("email")} required className="bg-[#F4F4F5] border-black/10 rounded-none h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="input-password" value={form.password} onChange={set("password")} required className="bg-[#F4F4F5] border-black/10 rounded-none h-11" />
            </div>
            {mode === "register" && (
              <div className="space-y-2">
                <Label>I am a…</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="select-role" className="bg-[#F4F4F5] border-black/10 rounded-none h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#F4F4F5] border-black/10">
                    <SelectItem value="candidate">Candidate</SelectItem>
                    <SelectItem value="client">Client / Hiring Company</SelectItem>
                    <SelectItem value="internal">Internal Recruiter</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {error && (
              <p data-testid="auth-error" className="text-sm text-destructive bg-destructive/10 border border-destructive/30 px-3 py-2">
                {error}
              </p>
            )}

            <Button type="submit" data-testid="submit-btn" disabled={loading} className="w-full h-11 rounded-none bg-primary hover:bg-[#09090B] hover:text-white transition-colors font-medium">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "login" ? "Sign In" : "Create Account"}
            </Button>
          </form>

          <Link to="/forgot-password" data-testid="forgot-link" className="block text-center text-sm text-muted-foreground hover:text-[#09090B] transition-colors mt-4">
            Forgot your password?
          </Link>
        </div>
      </div>
    </div>
  );
}
